import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Login from "app/containers/Login";
import WebReportV1 from "app/containers/WebReportV1";
import Dashboard from "app/containers/Dashboard";
import Templates from "app/containers/Templates";
import CreateTemplate from "app/containers/Templates/CreateTemplate";
import TemplateDetails from "app/containers/Templates/TemplateDetails";
import SmartTemplateIntro from "app/containers/SmartTemplateIntro";
import GlobalNavBar from "app/components/WebApp/GlobalNavBar";
import CreateAccount from "../containers/CreateAccount";
import VerifyEmail from "../containers/VerifyEmail";
import ResetPassword from "../containers/CreateAccount/resetPassword";
import RegisterSuccess from "../containers/CreateAccount/registerSuccess";
import AppDownload from "../containers/CreateAccount/appDownload";
import BackgroundSurvey from "../containers/CreateAccount/backgroundSurvey";
import UserInfo from "../containers/UserInfo";
import ResetPasswordAuthenticated from "../containers/CreateAccount/resetPasswordAuthenticated";
import Logout from "app/containers/Logout";
import { useSelector } from "react-redux";
import { useApp } from "app/contexts/AppContext";
import Sidebar from "app/components/WebApp/Sidebar";
import LoadingSpinner from "app/components/LoadingSpinner";
import JobCalendar from "app/containers/JobCalendar";
import AgentManagement from "app/containers/AgentManagement";
import NewAgent from "app/containers/AgentManagement/NewAgent";
import DisclaimerSettings from "app/containers/DisclaimerSettings";
import "./routes.css";

// web report example URL: /compiled-reports/e31a9484-4117-454f-b2f7-c98600f516a6

const getAuthStatus = () => {
  const token = localStorage.getItem("token");
  return token;
};

const AuthenticatedLayout = ({ children }) => {
  const {
    sidebar: { isCollapsed },
  } = useApp();

  return (
    <div className="authenticated-layout">
      <GlobalNavBar />
      <div className="app-content">
        <Sidebar />
        <main
          className={`app-main-content ${
            !isCollapsed ? "expanded" : "collapsed"
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

const PrivateRoute = ({ Component }) => {
  const { token } = useSelector((state) => state.login);
  const { auth, survey } = useApp();
  const currentPath = window.location.pathname;

  // If not authenticated, redirect to login
  if (!token) {
    return <Navigate to="/login" />;
  }

  // Show loading while app is initializing (this prevents the flash)
  if (!auth.isAppInitialized) {
    return <LoadingSpinner />;
  }

  // After app is initialized, check survey completion
  if (!survey.isCompleted && currentPath !== "/background-survey") {
    return <Navigate to="/background-survey" replace />;
  }

  return (
    <AuthenticatedLayout>
      <Component />
    </AuthenticatedLayout>
  );
};

// Update ProtectedPublicRoute to handle survey completion
const ProtectedPublicRoute = ({ Component }) => {
  const { token } = useSelector((state) => state.login);
  const { auth, survey } = useApp();
  const navigate = useNavigate();

  // After app is initialized, check if survey is completed
  useEffect(() => {
    if (auth.isAppInitialized && survey.isCompleted) {
      navigate("/", { replace: true });
    }
  }, [survey.isCompleted, auth.isAppInitialized, navigate]);

  // If not authenticated, redirect to login
  if (!token) {
    return <Navigate to="/login" />;
  }

  // Show loading while app is initializing
  if (!auth.isAppInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <GlobalNavBar />
      <div className="public-layout">
        <Component />
      </div>
    </>
  );
};

// Keep PublicRoute as a component wrapper, similar to PrivateRoute
const PublicRoute = ({ Component }) => {
  const isAuthenticated = getAuthStatus();
  const currentPath = window.location.pathname;

  // Allow register-success and reset-password even when authenticated
  const allowedAuthenticatedPaths = ["/register-success", "/reset-password"];
  if (isAuthenticated && !allowedAuthenticatedPaths.includes(currentPath)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <GlobalNavBar />
      <div className="public-layout">
        <Component />
      </div>
    </>
  );
};

// Define route types
export const ROUTE_TYPES = {
  PUBLIC: "public",
  PROTECTED: "protected",
  STANDALONE: "standalone",
};

const publicRoutes = [
  {
    path: "/login",
    element: <PublicRoute Component={Login} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Login",
  },
  {
    path: "/create-account",
    element: <PublicRoute Component={CreateAccount} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Create Account",
  },
  {
    path: "/verify-email",
    element: <PublicRoute Component={VerifyEmail} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Verify Email",
  },
  {
    path: "/reset-password",
    element: <PublicRoute Component={ResetPassword} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Reset Password",
  },
  {
    path: "/app-download",
    element: <PublicRoute Component={AppDownload} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Download App",
  },
  {
    path: "/register-success",
    element: <PublicRoute Component={RegisterSuccess} />,
    type: ROUTE_TYPES.PUBLIC,
    title: "Registration Successful",
  },
];

// Update routes to use withSurveyCheck
const protectedRoutes = [
  {
    path: "/",
    element: <PrivateRoute Component={Dashboard} />,
    title: "Dashboard",
  },
  {
    path: "/templates",
    element: <PrivateRoute Component={Templates} />,
    title: "Templates",
  },
  {
    path: "/templates/new",
    element: <PrivateRoute Component={CreateTemplate} />,
    title: "Create Template",
  },
  {
    path: "/templates/:id",
    element: <PrivateRoute Component={TemplateDetails} />,
    title: "Template Details",
  },
  {
    path: "/job-calendar",
    element: <PrivateRoute Component={JobCalendar} />,
    title: "Job Calendar",
  },
  {
    path: "/agent-management",
    element: <PrivateRoute Component={AgentManagement} />,
    title: "Agent Management",
  },
  {
    path: "/agent-management/new",
    element: <PrivateRoute Component={NewAgent} />,
    title: "New Agent",
  },
  {
    path: "/agent-management/:id/edit",
    element: <PrivateRoute Component={NewAgent} />,
    title: "Edit Agent",
  },
  {
    path: "/disclaimer-setting",
    element: <PrivateRoute Component={DisclaimerSettings} />,
    title: "Disclaimer Setting",
  },
  {
    path: "/background-survey",
    element: <ProtectedPublicRoute Component={BackgroundSurvey} />,
    title: "Background Survey",
  },
  {
    path: "/user-info",
    element: <PrivateRoute Component={UserInfo} />,
    title: "User Info",
  },
  {
    path: "/reset-password-authenticated",
    element: <PrivateRoute Component={ResetPasswordAuthenticated} />,
    title: "Reset Password",
  },
  {
    path: "/logout",
    element: <PrivateRoute Component={Logout} />,
    title: "Logout",
  },
];

// Add back the standalone routes
const standaloneRoutes = [
  {
    path: "/compiled-reports/:reportId/*",
    element: <WebReportV1 />,
    title: "Web Report",
  },
  {
    path: "/smart-template-intro",
    element: <SmartTemplateIntro />,
    title: "Smart Template Introduction",
  },
  {
    path: "*",
    element: <Navigate to="/login" replace />,
    title: "Morph Inspect",
  },
];

// Helper function to check if a route is public
export const isPublicRoute = (pathname) => {
  return publicRoutes.some((route) => route.path === pathname);
};

// Helper function to check if force logout should be skipped for this route
// Returns true for public and standalone routes where 401 should not trigger logout
export const shouldSkipForceLogout = (pathname) => {
  // Standalone route patterns
  const standalonePatterns = [
    /^\/compiled-reports\//, // Web report routes
    /^\/smart-template-intro$/, // Smart template intro
  ];
  const isStandalone = standalonePatterns.some((pattern) =>
    pattern.test(pathname)
  );

  return isPublicRoute(pathname) || isStandalone;
};

// Update routes array
export const routes = [
  ...standaloneRoutes,
  ...publicRoutes,
  ...protectedRoutes,
];
